import React from 'react';
import { render, hydrate } from 'react-dom';
import { Provider } from 'react-redux';
import { OidcProvider } from 'redux-oidc';
import LanguageProvider from '@react/components/language/languageProvider.component';
import { ConnectedRouter } from 'connected-react-router/immutable';
import { MuiThemeProvider } from '@material-ui/core/styles';
import CssBaseline from '@material-ui/core/CssBaseline';
import { createBrowserHistory } from 'history';
import { loadableReady } from '@loadable/component';
import { fromJS } from 'immutable';
import { LOCAL_STORAGE, OIDC, SESSION_STORAGE } from '@constants/common';
import { API_ENDPOINT } from '@constants/api';
import { SITE_URLS } from '@constants/routes';
import { UserManager } from 'oidc-client';
import { IOidcConfiguration } from './models/oidcConfiguration';
import theme from './theme/theme';
import 'bootstrap/dist/css/bootstrap.min.css';
import configureStore from '../redux/configureStore';
import App from './app';
import userManager from './utils/oidcConfig.util';
import CustomOidcProvider from './components/oidc/customOidcProvider.component';
import { decodeOidcConfiguration } from './utils/common.util';

const history = createBrowserHistory();
let isRedirectingToLogin = false;

const installUnauthorizedHandler = () => {
  const originalFetch = window.fetch.bind(window);

  window.fetch = (input: RequestInfo, init?: RequestInit) => {
    return originalFetch(input, init).then((response) => {
      const requestUrl = typeof input === 'string' ? input : input.url;
      const isApiRequest = requestUrl.includes('/api/');
      const isLoginRequest = requestUrl.includes(API_ENDPOINT.LOGIN_ENDPOINT);

      if (response.status === 401 && isApiRequest && !isLoginRequest &&
        !isRedirectingToLogin && window.location.pathname !== SITE_URLS.LOGIN) {
        isRedirectingToLogin = true;
        sessionStorage.removeItem(SESSION_STORAGE.OIDC_USER);
        sessionStorage.setItem(
          SESSION_STORAGE.REDIRECT_URL,
          encodeURIComponent(window.location.pathname + window.location.search)
        );
        localStorage.removeItem(LOCAL_STORAGE.ACCESS_TOKEN);
        localStorage.removeItem(LOCAL_STORAGE.USER_INFO);
        window.location.assign(SITE_URLS.LOGIN);
      }

      return response;
    });
  };
};

installUnauthorizedHandler();

const rootElement = document.getElementById('react-app');
const oidcConfigurationElement = document.querySelector(`#${OIDC.CONFIGURATION.HTML_ID}`);

let oidcConfiguration: IOidcConfiguration = {};
if (oidcConfigurationElement) {
  oidcConfiguration = decodeOidcConfiguration(oidcConfigurationElement.innerHTML);
  oidcConfigurationElement.parentElement.removeChild(oidcConfigurationElement);
}
const userManagerInstance: UserManager = userManager(oidcConfiguration);
const store = configureStore(undefined, history, userManagerInstance);

loadableReady(() => {
  hydrate(
    <MuiThemeProvider theme={theme}>
      <Provider store={store}>
        <CustomOidcProvider store={store} userManager={userManagerInstance}>
          <LanguageProvider>
            <ConnectedRouter history={history}>
              <CssBaseline />
              <App />
            </ConnectedRouter>
          </LanguageProvider>
        </CustomOidcProvider>
      </Provider>
    </MuiThemeProvider>,
    rootElement);
});